import { useQuery } from "@tanstack/react-query"
import { AlertCircle, ArrowLeft } from "lucide-react"
import { Link, useParams } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { BadgeEstatus, type Estatus } from "@/componentes/expediente/BadgeEstatus"
import { LineaDeTiempo, type Transicion } from "@/componentes/expediente/LineaDeTiempo"
import { PanelDocumentos } from "@/componentes/expediente/PanelDocumentos"
import { comoFecha } from "@/lib/fechas"

/**
 * Un despacho, visto por su importador.
 *
 * Reutiliza `LineaDeTiempo` y `PanelDocumentos` a propósito: son los mismos datos y
 * mantener dos versiones garantizaría que se separaran. Lo que el cliente **no** ve
 * lo decide el servidor, no esta pantalla:
 *
 * - `DialogoAvanzar` no se monta, y aunque se montara el endpoint responde 404 a un
 *   usuario CLIENTE.
 * - En `PanelDocumentos` los botones de subir y retirar dependen de permisos que el
 *   rol `cliente` no tiene, y el listado que llega ya viene sin los documentos
 *   internos porque el servicio filtra por `visible_cliente`.
 */

interface Detalle {
  id: number
  numero: string
  estado: "ABIERTO" | "CERRADO"
  estatus_operativo: Estatus
  fecha_apertura: string
  fecha_cierre: string | null
  transiciones: Transicion[]
}

export function DespachoDetalle() {
  const { id } = useParams<{ id: string }>()

  const consulta = useQuery({
    queryKey: ["expediente", Number(id)],
    queryFn: () => api.get<Detalle>(`/api/v1/expedientes/${id}`),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  })

  if (consulta.isPending) {
    return (
      <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando el despacho…
      </p>
    )
  }

  if (consulta.isError) {
    // El despacho de otro importador y uno inexistente dan el MISMO 404, y ésa es
    // la razón de que este mensaje no distinga: decir «no es suyo» ya confirmaría
    // que existe.
    const noExiste = consulta.error instanceof ErrorApi && consulta.error.estado === 404
    return (
      <div role="alert" className="py-12 text-center" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={24} aria-hidden="true" className="mx-auto mb-3" />
        <p>{noExiste ? "Ese despacho no existe" : "No se pudo cargar el despacho"}</p>
        <Link to="/portal" className="mt-4 inline-block text-sm underline">
          Volver a mis despachos
        </Link>
      </div>
    )
  }

  const exp = consulta.data

  return (
    <div>
      <Link
        to="/portal"
        className="mb-4 inline-flex items-center gap-2 text-sm"
        style={{ color: "var(--fg-muted)" }}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Mis despachos
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{exp.numero}</h1>
        <BadgeEstatus estatus={exp.estatus_operativo} />
        <span className="tabular text-sm" style={{ color: "var(--fg-muted)" }}>
          abierto el {exp.fecha_apertura}
          {exp.fecha_cierre ? ` · cerrado el ${comoFecha(exp.fecha_cierre)}` : ""}
        </span>
      </div>

      <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Documentos</h2>
      <div className="mb-8">
        <PanelDocumentos expedienteId={exp.id} />
      </div>

      <h2 className="mb-3 text-sm font-medium uppercase tracking-wide">Seguimiento</h2>
      <LineaDeTiempo transiciones={exp.transiciones} />
    </div>
  )
}
